import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef, useState } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import Heading from '../../atoms/heading/Heading';
import type {
  HeaderFullWidthTitleProps,
  HeaderFullWidthTitleRef,
} from './HeaderFullWidthTitle.data';
import styles from './HeaderFullWidthTitle.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './HeaderFullWidthTitle.transitions';

const getMobileCoefficient = (string: string): number => {
  const wordCount = string.trim().split(/\s+/).length;
  if (wordCount % 3 === 0) return 3;
  if (wordCount % 2 === 0) return 2;
  return 1;
};

export default function HeaderFullWidthTitle({
  isVisible,
  className,
  blok,
  ref,
}: HeaderFullWidthTitleProps & { ref?: Ref<HeaderFullWidthTitleRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_1024);
  const isBelow1920 = useMobileViewport(sharedVariables.deviceState.MIN_1920);

  const titleRef = useRef<HTMLDivElement>(null);
  const headerFullWidthTitleRef = useRef<HTMLDivElement>(null);
  const contentWidthTitleRef = useRef<HTMLDivElement>(null);

  // Used to refresh the component after inline fontSize change
  const [, setFontSize] = useState(0);

  useIsomorphicLayoutEffect(() => {
    if (!headerFullWidthTitleRef?.current?.clientWidth) return;
    if (!contentWidthTitleRef?.current?.offsetWidth) return;
    if (!titleRef?.current) return;

    let scale =
      headerFullWidthTitleRef.current.clientWidth / contentWidthTitleRef.current.offsetWidth;

    if (!scale || Number.isNaN(scale)) {
      scale = 1;
    }

    const fontSize = window.getComputedStyle(titleRef.current, null).getPropertyValue('font-size');

    let mobileCoefficient = 1;
    if (isMobileViewport && scale > 1.01) {
      // make the text on one word per lines for mobile
      mobileCoefficient = getMobileCoefficient(blok.title);
    }
    if (!isMobileViewport && !isBelow1920 && scale < 0.99) {
      // make the text on one line for desktop
      mobileCoefficient = 1 / getMobileCoefficient(blok.title);
    }

    const newFontSize = Math.max(scale * parseInt(fontSize, 10) * mobileCoefficient, 90);
    titleRef.current.style.fontSize = `${newFontSize}px`;

    // Scale check to prevent infinity loops caused by 1px changes
    if (scale > 1.01 || scale < 0.99) {
      document.fonts.ready.then(() => {
        setFontSize(newFontSize);
      });
    }
  }, [
    headerFullWidthTitleRef?.current?.clientWidth,
    contentWidthTitleRef?.current?.offsetWidth,
    isMobileViewport,
    blok.title,
  ]);

  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.fullWidthTitle, className)}
      {...sbEditable(blok)}
    >
      <Heading
        as="h1"
        size="heading-01"
        disableTriggerInOnScroll={false}
        transitionInType="slideUpFadeIn"
        className={styles.title}
        ref={titleRef}
      >
        <div ref={headerFullWidthTitleRef} className={styles.insideTitleContainer}>
          <div ref={contentWidthTitleRef} className={styles.insideTitle}>
            {blok.title}
          </div>
        </div>
      </Heading>
    </div>
  );
}
